import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
    Plus, Edit, Trash2, Eye, EyeOff, LogOut, 
    FileText, Upload, X, Save, Home, Image as ImageIcon
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showEditor, setShowEditor] = useState(false);
    const [editingPost, setEditingPost] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        slug: '',
        excerpt: '',
        content: '',
        image_url: '',
        published: false
    });
    const [uploading, setUploading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        checkAuth();
        fetchPosts();
    }, []);

    const getToken = () => localStorage.getItem('admin_token');

    const checkAuth = async () => {
        const token = getToken();
        if (!token) {
            navigate('/admin');
            return;
        }
        try {
            await axios.get(`${API}/auth/verify`, {
                headers: { Authorization: `Bearer ${token}` }
            });
        } catch (err) {
            localStorage.removeItem('admin_token');
            navigate('/admin');
        }
    };

    const fetchPosts = async () => {
        try {
            const response = await axios.get(`${API}/blog/posts?published_only=false`, {
                headers: { Authorization: `Bearer ${getToken()}` }
            });
            setPosts(response.data);
        } catch (err) {
            console.error('Error fetching posts:', err);
        } finally {
            setLoading(false);
        }
    };

    const generateSlug = (title) => {
        return title
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .trim();
    };

    const handleTitleChange = (e) => {
        const title = e.target.value;
        setFormData(prev => ({
            ...prev,
            title,
            slug: editingPost ? prev.slug : generateSlug(title)
        }));
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploading(true);
        const uploadData = new FormData();
        uploadData.append('file', file);

        try {
            const response = await axios.post(`${API}/upload/image`, uploadData, {
                headers: {
                    Authorization: `Bearer ${getToken()}`,
                    'Content-Type': 'multipart/form-data'
                }
            });
            setFormData(prev => ({
                ...prev,
                image_url: `${process.env.REACT_APP_BACKEND_URL}${response.data.image_url}`
            }));
            toast.success('Image uploaded successfully');
        } catch (err) {
            toast.error('Failed to upload image');
        } finally {
            setUploading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        try {
            if (editingPost) {
                await axios.put(`${API}/blog/posts/${editingPost.id}`, formData, {
                    headers: { Authorization: `Bearer ${getToken()}` }
                });
                toast.success('Post updated successfully');
            } else {
                await axios.post(`${API}/blog/posts`, formData, {
                    headers: { Authorization: `Bearer ${getToken()}` }
                });
                toast.success('Post created successfully');
            }
            
            setShowEditor(false);
            setEditingPost(null);
            resetForm();
            fetchPosts();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to save post');
        }
    };

    const handleEdit = (post) => {
        setEditingPost(post);
        setFormData({
            title: post.title,
            slug: post.slug,
            excerpt: post.excerpt,
            content: post.content,
            image_url: post.image_url || '',
            published: post.published
        });
        setShowEditor(true);
    };

    const handleDelete = async (postId) => {
        if (!window.confirm('Are you sure you want to delete this post?')) return;

        try {
            await axios.delete(`${API}/blog/posts/${postId}`, {
                headers: { Authorization: `Bearer ${getToken()}` }
            });
            toast.success('Post deleted successfully');
            fetchPosts();
        } catch (err) {
            toast.error('Failed to delete post');
        }
    };

    const togglePublish = async (post) => {
        try {
            await axios.put(`${API}/blog/posts/${post.id}`, 
                { published: !post.published },
                { headers: { Authorization: `Bearer ${getToken()}` } }
            );
            toast.success(post.published ? 'Post unpublished' : 'Post published');
            fetchPosts();
        } catch (err) {
            toast.error('Failed to update post');
        }
    };

    const resetForm = () => {
        setFormData({
            title: '',
            slug: '',
            excerpt: '',
            content: '',
            image_url: '',
            published: false
        });
    };

    const handleLogout = () => {
        localStorage.removeItem('admin_token');
        navigate('/admin');
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    return (
        <div data-testid="admin-dashboard" className="min-h-screen bg-hope-light">
            {/* Sidebar */}
            <aside className="fixed left-0 top-0 bottom-0 w-64 admin-sidebar text-white p-6 hidden lg:block">
                <div className="flex items-center gap-3 mb-10">
                    <div className="w-10 h-10 rounded-xl bg-white text-hope-emerald flex items-center justify-center font-heading font-bold">
                        H
                    </div>
                    <span className="font-heading font-semibold">Hope Admin</span>
                </div>

                <nav className="space-y-2">
                    <Link
                        to="/admin/dashboard"
                        className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/10"
                    >
                        <FileText className="w-5 h-5" />
                        Blog Posts
                    </Link>
                    <Link
                        to="/"
                        className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-white/10 transition-colors"
                    >
                        <Home className="w-5 h-5" />
                        View Website
                    </Link>
                </nav>

                <button
                    onClick={handleLogout}
                    data-testid="admin-logout-btn"
                    className="absolute bottom-6 left-6 right-6 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white/10 hover:bg-white/20 transition-colors"
                >
                    <LogOut className="w-5 h-5" />
                    Logout
                </button>
            </aside>

            {/* Main Content */}
            <main className="lg:ml-64 p-6 lg:p-8">
                {/* Mobile Header */}
                <div className="lg:hidden flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-hope-emerald text-white flex items-center justify-center font-heading font-bold">
                            H
                        </div>
                        <span className="font-heading font-semibold text-hope-forest">Admin</span>
                    </div>
                    <button
                        onClick={handleLogout}
                        className="p-2 rounded-xl bg-gray-100 text-gray-600"
                    >
                        <LogOut className="w-5 h-5" />
                    </button>
                </div>

                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                    <div>
                        <h1 className="font-heading font-bold text-2xl text-hope-forest">
                            Blog Posts
                        </h1>
                        <p className="text-gray-500">Manage your blog content</p>
                    </div>
                    <button
                        onClick={() => {
                            setEditingPost(null);
                            resetForm();
                            setShowEditor(true);
                        }}
                        data-testid="add-post-btn"
                        className="btn-premium btn-primary flex items-center gap-2"
                    >
                        <Plus className="w-5 h-5" />
                        Add New Post
                    </button>
                </div>

                {/* Posts List */}
                {loading ? (
                    <div className="space-y-4">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="bg-white rounded-2xl p-6 shadow-sm">
                                <div className="h-6 w-1/2 skeleton rounded-lg mb-4" />
                                <div className="h-4 w-full skeleton rounded-lg" />
                            </div>
                        ))}
                    </div>
                ) : posts.length === 0 ? (
                    <div className="text-center py-16 bg-white rounded-3xl">
                        <FileText className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                        <p className="text-gray-500 text-lg mb-4">No posts yet</p>
                        <button
                            onClick={() => setShowEditor(true)}
                            className="text-hope-emerald font-medium hover:underline"
                        >
                            Create your first post
                        </button>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {posts.map((post, index) => (
                            <motion.div
                                key={post.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.05 }}
                                data-testid={`post-item-${index}`}
                                className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow"
                            >
                                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-2">
                                            <h3 className="font-heading font-semibold text-lg text-hope-forest">
                                                {post.title}
                                            </h3>
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                                post.published 
                                                    ? 'bg-green-100 text-green-700' 
                                                    : 'bg-yellow-100 text-yellow-700'
                                            }`}>
                                                {post.published ? 'Published' : 'Draft'}
                                            </span>
                                        </div>
                                        <p className="text-gray-500 text-sm line-clamp-1 mb-2">
                                            {post.excerpt}
                                        </p>
                                        <p className="text-gray-400 text-xs">
                                            Created: {formatDate(post.created_at)}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => togglePublish(post)}
                                            data-testid={`toggle-publish-${index}`}
                                            className={`p-2 rounded-xl transition-colors ${
                                                post.published 
                                                    ? 'bg-green-50 text-green-600 hover:bg-green-100' 
                                                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                            }`}
                                            title={post.published ? 'Unpublish' : 'Publish'}
                                        >
                                            {post.published ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                                        </button>
                                        <button
                                            onClick={() => handleEdit(post)}
                                            data-testid={`edit-post-${index}`}
                                            className="p-2 rounded-xl bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                                            title="Edit"
                                        >
                                            <Edit className="w-5 h-5" />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(post.id)}
                                            data-testid={`delete-post-${index}`}
                                            className="p-2 rounded-xl bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                                            title="Delete"
                                        >
                                            <Trash2 className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                )}
            </main>

            {/* Post Editor Modal */}
            {showEditor && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-white rounded-3xl w-full max-w-3xl max-h-[90vh] overflow-y-auto my-8"
                    >
                        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-3xl">
                            <h2 className="font-heading font-semibold text-xl text-hope-forest">
                                {editingPost ? 'Edit Post' : 'Create New Post'}
                            </h2>
                            <button
                                onClick={() => {
                                    setShowEditor(false);
                                    setEditingPost(null);
                                    resetForm();
                                }}
                                className="p-2 rounded-xl hover:bg-gray-100 transition-colors"
                            >
                                <X className="w-5 h-5 text-gray-500" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6 space-y-6" data-testid="post-editor-form">
                            {/* Title */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Title *
                                </label>
                                <input
                                    type="text"
                                    value={formData.title}
                                    onChange={handleTitleChange}
                                    required
                                    data-testid="post-title-input"
                                    className="form-input"
                                    placeholder="Enter post title"
                                />
                            </div>

                            {/* Slug */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    URL Slug *
                                </label>
                                <input
                                    type="text"
                                    value={formData.slug}
                                    onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                                    required
                                    data-testid="post-slug-input"
                                    className="form-input"
                                    placeholder="url-friendly-slug"
                                />
                            </div>

                            {/* Excerpt */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Excerpt *
                                </label>
                                <textarea
                                    value={formData.excerpt}
                                    onChange={(e) => setFormData(prev => ({ ...prev, excerpt: e.target.value }))}
                                    required
                                    rows={2}
                                    data-testid="post-excerpt-input"
                                    className="form-textarea"
                                    placeholder="Brief description of the post"
                                />
                            </div>

                            {/* Image Upload */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Featured Image
                                </label>
                                <div className="flex flex-col gap-4">
                                    {formData.image_url && (
                                        <div className="relative">
                                            <img
                                                src={formData.image_url}
                                                alt="Preview"
                                                className="w-full h-48 object-cover rounded-xl"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setFormData(prev => ({ ...prev, image_url: '' }))}
                                                className="absolute top-2 right-2 p-1 rounded-full bg-red-500 text-white"
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                    )}
                                    <div className="flex gap-4">
                                        <label className="flex-1 cursor-pointer">
                                            <div className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-dashed border-gray-200 hover:border-hope-emerald transition-colors">
                                                <Upload className="w-5 h-5 text-gray-400" />
                                                <span className="text-gray-500">
                                                    {uploading ? 'Uploading...' : 'Upload Image'}
                                                </span>
                                            </div>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={handleImageUpload}
                                                className="hidden"
                                                disabled={uploading}
                                            />
                                        </label>
                                        <div className="flex-1">
                                            <input
                                                type="url"
                                                value={formData.image_url}
                                                onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                                                data-testid="post-image-url-input"
                                                className="form-input h-full"
                                                placeholder="Or paste image URL"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Content */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Content * (HTML supported)
                                </label>
                                <textarea
                                    value={formData.content}
                                    onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                                    required
                                    rows={10}
                                    data-testid="post-content-input"
                                    className="form-textarea font-mono text-sm"
                                    placeholder="<p>Write your content here...</p>"
                                />
                            </div>

                            {/* Published Toggle */}
                            <div className="flex items-center gap-3">
                                <input
                                    type="checkbox"
                                    id="published"
                                    checked={formData.published}
                                    onChange={(e) => setFormData(prev => ({ ...prev, published: e.target.checked }))}
                                    data-testid="post-published-checkbox"
                                    className="w-5 h-5 rounded border-gray-300 text-hope-emerald focus:ring-hope-emerald"
                                />
                                <label htmlFor="published" className="text-gray-700">
                                    Publish immediately
                                </label>
                            </div>

                            {/* Actions */}
                            <div className="flex gap-4 pt-4">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowEditor(false);
                                        setEditingPost(null);
                                        resetForm();
                                    }}
                                    className="flex-1 px-6 py-3 rounded-xl border border-gray-200 text-gray-600 font-medium hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    data-testid="save-post-btn"
                                    className="flex-1 btn-premium btn-primary flex items-center justify-center gap-2"
                                >
                                    <Save className="w-5 h-5" />
                                    {editingPost ? 'Update Post' : 'Create Post'}
                                </button>
                            </div>
                        </form>
                    </motion.div>
                </div>
            )}
        </div>
    );
};

export default AdminDashboard;
